// @flow
import React from 'react';
import { get, map, isEqual, filter, findIndex } from 'lodash';
import type { Record, Attachment as AttachmentType, AttachmentsState } from '@types';
import moment from 'moment';
import { isPresent, sort, numberCompare, createNsTranslator, handleMomenti18n, isCordovaApp, isPdfFile } from '@helpers/helpers';
import { PDFPreview, Gallery } from '@elements';
import type { PDFDoc } from '../../../../elements/PDFPreview/PDFPreview';
import { FileAttachment } from '@blocks';
import { withTranslation } from 'react-i18next';
import './Attachment.scss';

type Props ={
    record: Record,
    attachments: Array<AttachmentType>,
    allAttachments: AttachmentsState,
    onDeleteAttachment: Function,
    onDownloadAttachment: Function,
    t: Function,
    i18n: Object,
}

type State = {
    pdf: ?{ docs: Array<PDFDoc>, index: number },
}

const fileAttachmentDropdownList = (_: Function) => [
  { text: _('action/download'), action: 'download' },
  { text: _('action/delete_file'), action: 'delete' },
];
class Attachment extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      pdf: null,
    };
  }

  shouldComponentUpdate(next: Props, nState: State) {
    return !isEqual(this.props.record, next.record)
      || !isEqual(this.props.attachments, next.attachments)
      || !isEqual(this.state, nState)
      || !isEqual(this.props.allAttachments, next.allAttachments);
  }

  render () {
    const { record, attachments } = this.props;

    handleMomenti18n(this.props.i18n, moment);

    const _ = createNsTranslator('timeline', this.props.t);

    const date = moment(get(record, 'date'));

    const files = filter(attachments, item => !isPresent(get(item, 'thumbUrl')));

    const filesItems = map<any, any>(files, (file: AttachmentType) => (
      <FileAttachment
        file={file}
        fileAttachmentDropdownList={fileAttachmentDropdownList(_)}
        onSelectFileAction={this.onSelectFileAction}
        onOpenPDF={this.openPdf}
      />
    ));
    return (
      <div className="attachmentViewItem">
        {this.state.pdf && (
        <PDFPreview
          docs={this.state.pdf.docs}
          startIndex={this.state.pdf.index}
          onClose={this.closePdfDoc}
        />
        )}

        <div className="attachmentBody">
          <div className="attachmentThumbs">
            <Gallery images={this.getThumbs()} onClickDownload={this.handleDownloadImage} />
          </div>
          <br />
          <div className="fileAttachments">{filesItems}</div>
        </div>
        <div className="attachinfo">
          <span className="attachmentDate"> {date.isValid() ? date.format(_('format/date_01')) : 'Invalid Date'}</span>
          <span className="attachmentDoctor"> {get(record, 'userName', '')}</span>
        </div>
      </div>
    );
  }

  getThumbs = () => sort(filter(this.props.attachments, item => isPresent(get(item, 'thumbUrl'))), numberCompare('id'));

  onSelectFileAction = (file: AttachmentType) => (item: Object) => {
    const { onDeleteAttachment, record } = this.props;
    if (item.action === 'download') {
      const win = window.open(file.downloadUrl, '_blank');
      win.focus();
      this.props.onDownloadAttachment(record.id, file.id);
    }

    if (item.action === 'delete') {
      onDeleteAttachment(record.id, file.id);
    }
  };

  onDeleteAttachment = (file: AttachmentType) => () => {
    const { onDeleteAttachment, record } = this.props;
    onDeleteAttachment(record.id, file.id);
  };

  closePdfDoc = () => this.setState({ pdf: null });

  // The record's PDFs, one after the other, as on the record itself (RecordElement's onOpenPDF)
  openPdf = (file: AttachmentType) => {
    const { record, onDownloadAttachment } = this.props;
    const pdfs = filter(this.props.attachments, item => !isPresent(get(item, 'thumbUrl')) && isPdfFile(item));
    const docs = map(pdfs, (pdf: AttachmentType) => {
      const load = () => new Promise(resolve => onDownloadAttachment(record.id, pdf.id, resolve, true));
      return isCordovaApp()
        ? { key: String(pdf.id), name: pdf.name, load, onDownload: () => onDownloadAttachment(record.id, pdf.id) }
        : { key: String(pdf.id), name: pdf.name, url: pdf.downloadUrl, load };
    });
    this.setState({ pdf: { docs, index: Math.max(0, findIndex(pdfs, pdf => pdf.id === file.id)) } });
  };

  handleDownloadImage = (id: number, callback: Function) => {
    const { onDownloadAttachment, record } = this.props;
    onDownloadAttachment(record.id, id, callback);
  }
}

export default withTranslation()(Attachment);
